// src/lib/etfCalculator.js
//
// v3.5.99.7.13 — Early Termination Fee calculator + letter generator.
//
// Ferrum's standard MSA uses full-buyout: ETF = MRR × months remaining.
// If a client wants to terminate a 36-month contract after 26 months,
// they owe the next 10 months at the agreed MRR.
//
// Calendar-month math (NOT decimal/day-prorated). Reasoning: contract
// language references "months", reps will reference printed months, and
// "26 months elapsed, 10 remaining" is unambiguous in a legal letter.
// Partial months at the boundary don't count toward elapsed — they're
// only "complete" once the day-of-month anchor is met.
//
// Example: contract started 2024-03-15, terminating 2026-05-14 →
//   2 years × 12 = 24 months, plus (May − March) = +2 → 26
//   but terminationDay (14) < startDay (15) → -1
//   = 25 complete months elapsed. (May 15 would have been the 26th.)
//
// Two pure exports:
//   - calculateEtf({ mrr, term, contractStart, terminationDate })
//       → { monthsElapsed, monthsRemaining, etfAmount, warnings }
//   - buildEtfLetterHTML({ clientName, clientAddress, mrr, term,
//                           contractStart, terminationDate, ferrumInfo })
//       → string (full <html>...</html>)

/**
 * Compute full calendar months between two dates, exclusive of partial.
 * Returns 0 if end < start.
 */
function fullCalendarMonthsBetween(startDate, endDate) {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return 0;
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 0;
  if (endDate < startDate) return 0;

  let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
  months += endDate.getMonth() - startDate.getMonth();
  // If we haven't reached the anchor day-of-month in the final month yet,
  // the current month isn't "complete" — back it out.
  if (endDate.getDate() < startDate.getDate()) months--;
  return Math.max(0, months);
}

/**
 * Main calculation.
 *
 * @param {object} opts
 * @param {number} opts.mrr             — monthly recurring revenue, dollars/mo
 * @param {number} opts.term            — full contract term in months (e.g. 36)
 * @param {Date|string} opts.contractStart    — contract effective date
 * @param {Date|string} opts.terminationDate  — termination effective date
 * @returns {{
 *   monthsElapsed: number,
 *   monthsRemaining: number,
 *   etfAmount: number,
 *   warnings: string[],
 *   contractEndDate: Date | null,
 * }}
 */
export function calculateEtf({ mrr, term, contractStart, terminationDate }) {
  const warnings = [];

  const mrrN  = Number(mrr) || 0;
  const termN = parseInt(term, 10);

  const start = contractStart instanceof Date ? contractStart : new Date(contractStart);
  const end   = terminationDate instanceof Date ? terminationDate : new Date(terminationDate);

  // Validation — collect non-blocking warnings; caller decides how to display.
  if (!mrrN || mrrN <= 0)      warnings.push('MRR must be greater than zero.');
  if (!termN || termN <= 0)    warnings.push('Contract term must be greater than zero months.');
  if (isNaN(start.getTime()))  warnings.push('Contract start date is invalid.');
  if (isNaN(end.getTime()))    warnings.push('Termination date is invalid.');
  if (warnings.length) {
    return { monthsElapsed: 0, monthsRemaining: 0, etfAmount: 0, warnings, contractEndDate: null };
  }

  // Contract end date = start + term months (anchored to start's day-of-month).
  const contractEndDate = new Date(start);
  contractEndDate.setMonth(contractEndDate.getMonth() + termN);

  if (end < start) {
    warnings.push('Termination date is before the contract start. ETF assumes the full contract value.');
    return {
      monthsElapsed: 0,
      monthsRemaining: termN,
      etfAmount: mrrN * termN,
      warnings,
      contractEndDate,
    };
  }

  if (end >= contractEndDate) {
    warnings.push('Termination is on or after contract end date. No ETF is owed.');
    return {
      monthsElapsed: termN,
      monthsRemaining: 0,
      etfAmount: 0,
      warnings,
      contractEndDate,
    };
  }

  const monthsElapsed   = fullCalendarMonthsBetween(start, end);
  const monthsRemaining = Math.max(0, termN - monthsElapsed);
  const etfAmount       = mrrN * monthsRemaining;

  return { monthsElapsed, monthsRemaining, etfAmount, warnings, contractEndDate };
}

/**
 * Format a date as "Month D, YYYY" for the letter.
 */
function fmtLetterDate(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * USD currency formatter.
 */
function fmtUSD(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

/**
 * HTML escape for any user-controlled strings going into the letter body.
 * Defense in depth — even though we control the inputs, never trust
 * names/addresses that could carry stray <, >, &.
 */
function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Build the full HTML letter ready for print or send.
 * Caller is responsible for opening the result (window.open + write +
 * trigger print) or saving.
 *
 * @param {object} opts
 * @param {string} opts.clientName
 * @param {string} opts.clientAddress
 * @param {number} opts.mrr
 * @param {number} opts.term
 * @param {Date}   opts.contractStart
 * @param {Date}   opts.terminationDate
 * @param {object} opts.ferrumInfo  — { name, address, phone, website, logoUrl }
 *                                    logoUrl from pricing_settings.company_logo_url
 * @param {string} [opts.signatureMode='none']  — 'none' for human print,
 *                                    'two_party' embeds SignWell text tags
 *                                    {{signature:1:y}}/{{date:1}} for Ferrum
 *                                    and {{signature:2:y}}/{{date:2}} for Client.
 *                                    Tags wrapped in white-on-white spans so
 *                                    they're visually invisible but SignWell
 *                                    detects them for field overlay.
 * @returns {string} — full <html>...</html> document
 */
export function buildEtfLetterHTML(opts) {
  const {
    clientName, clientAddress,
    mrr, term, contractStart, terminationDate,
    ferrumInfo = {},
    signatureMode = 'none',
    contractReference = '',  // v7.14.2: e.g. quote_number like "QT-2024-0042"
                              // — renders in opening paragraph + summary table top row.
                              //   Empty string = both spots omitted gracefully.
    letterRef = '',           // v3.5.99.7.19: short ETF letter UID (e.g. "ETF-a1b2c3d4")
                              //   rendered in letterhead so the PDF can be traced
                              //   back to its etf_letters row. Caller passes the
                              //   first 8 chars of the row's UUID prefixed with
                              //   "ETF-". Empty string = nothing rendered.
  } = opts;

  const calc = calculateEtf({ mrr, term, contractStart, terminationDate });
  const today = new Date();

  // The letter renders even when no ETF is owed — useful as a written
  // confirmation that the client may terminate without penalty.
  const isNoEtf = calc.etfAmount === 0 && !calc.warnings.some(w => w.includes('invalid'));

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Early Termination Notice — ${esc(clientName) || 'Client'}</title>
  <style>
    /* v3.5.99.7.14.1: density pass. Previous version overflowed onto a
       second page due to double margins (PDFShift outer + body padding
       inner) plus generous internal section spacing. Changes:
       - PDFShift margin reduced from 0.5in to 0.4in (caller-side)
       - Body padding (was 0.5in vertical) removed entirely
       - max-width loosened from 7in to 7.4in to use the wider canvas
       - Logo max-height 56→42px (still readable, less vertical real estate)
       - Body font 11pt→10.5pt, line-height 1.5→1.45
       - h1 14pt→12pt with tighter top margin
       - p margin 10px→7px
       - Contract summary padding 14/16→10/14, td padding 4→2.5
       - ETF total padding 16→12, margin 24→14, amount 24pt→20pt
       - Signature block margin-top 48→26
       - Footer margin-top 36→16
    */
    @page { size: letter; margin: 0.4in; }
    body { font-family: Georgia, 'Times New Roman', serif; font-size: 10.5pt; line-height: 1.45; color: #1f2937; max-width: 7.4in; margin: 0 auto; padding: 0; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px; padding-bottom: 10px; border-bottom: 2px solid #0f1e3c; }
    .header .from { font-size: 10pt; color: #4b5563; line-height: 1.35; }
    .header .from .company-name { font-weight: 700; color: #0f1e3c; font-size: 11pt; margin-bottom: 3px; font-family: 'Helvetica Neue', Arial, sans-serif; letter-spacing: 0.02em; }
    .date { text-align: right; font-size: 10pt; color: #4b5563; }
    .to { margin-bottom: 14px; font-size: 10.5pt; }
    .to .client-name { font-weight: 700; color: #0f1e3c; }
    h1 { font-size: 12pt; color: #0f1e3c; margin: 16px 0 8px; font-family: 'Helvetica Neue', Arial, sans-serif; letter-spacing: 0.02em; }
    p { margin: 7px 0; }
    .contract-summary { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 4px; padding: 10px 14px; margin: 12px 0; font-size: 9.5pt; }
    .contract-summary table { width: 100%; border-collapse: collapse; }
    .contract-summary td { padding: 2.5px 0; }
    .contract-summary td:first-child { color: #6b7280; width: 45%; }
    .contract-summary td:last-child { font-weight: 600; color: #0f1e3c; text-align: right; }
    .etf-total { background: ${isNoEtf ? '#dcfce7' : '#fef3c7'}; border: 1px solid ${isNoEtf ? '#86efac' : '#fde68a'}; border-radius: 4px; padding: 12px; margin: 14px 0; text-align: center; }
    .etf-total .label { font-size: 9.5pt; color: ${isNoEtf ? '#166534' : '#92400e'}; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; margin-bottom: 4px; }
    .etf-total .amount { font-size: 20pt; font-weight: 700; color: ${isNoEtf ? '#166534' : '#0f1e3c'}; font-family: 'Helvetica Neue', Arial, sans-serif; }
    .etf-total .formula { font-size: 9pt; color: #6b7280; margin-top: 4px; }
    .signature-block { display: flex; justify-content: space-between; margin-top: 26px; gap: 28px; }
    .signature-block .sig-col { flex: 1; }
    .signature-block .sig-line { border-bottom: 1px solid #6b7280; height: 28px; margin-bottom: 4px; }
    .signature-block .sig-label { font-size: 8.5pt; color: #6b7280; }
    .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 8pt; color: #9ca3af; text-align: center; }
    @media screen { body { background: white; box-shadow: 0 4px 16px rgba(0,0,0,0.08); padding: 0.5in; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="from">
      ${ferrumInfo.logoUrl
        ? `<div style="margin-bottom: 6px;"><img src="${esc(ferrumInfo.logoUrl)}" alt="${esc(ferrumInfo.name) || 'Logo'}" style="max-height: 42px; max-width: 180px; object-fit: contain; display: block;" /></div>`
        : `<div class="company-name">${esc(ferrumInfo.name) || 'Ferrum Technology Services'}</div>`
      }
      ${ferrumInfo.logoUrl ? `<div class="company-name" style="font-size: 9.5pt; margin-bottom: 2px;">${esc(ferrumInfo.name) || 'Ferrum Technology Services'}</div>` : ''}
      ${ferrumInfo.address ? `<div>${esc(ferrumInfo.address)}</div>` : ''}
      ${ferrumInfo.phone   ? `<div>${esc(ferrumInfo.phone)}</div>`   : ''}
      ${ferrumInfo.website ? `<div>${esc(ferrumInfo.website)}</div>` : ''}
    </div>
    <div class="date">
      ${fmtLetterDate(today)}
      ${letterRef ? `<div style="font-family: 'DM Mono', Menlo, monospace; font-size: 8.5pt; color: #9ca3af; margin-top: 4px; letter-spacing: 0.05em;">${esc(letterRef)}</div>` : ''}
    </div>
  </div>

  <div class="to">
    <div class="client-name">${esc(clientName) || 'Client'}</div>
    ${clientAddress ? `<div style="white-space: pre-line; color: #4b5563;">${esc(clientAddress)}</div>` : ''}
  </div>

  <h1>${isNoEtf ? 'Service Agreement Termination Notice' : 'Early Termination Notice for Service Agreement'}</h1>

  <p>This letter ${isNoEtf ? 'confirms the termination of' : 'sets forth the early termination terms applicable to'}
  the Service Agreement${contractReference ? ` <strong>(Reference: ${esc(contractReference)})</strong>` : ''}
  between <strong>${esc(ferrumInfo.name) || 'Ferrum Technology Services'}</strong> ("Provider") and
  <strong>${esc(clientName) || 'the Client'}</strong> ("Client"),
  with a termination effective date of <strong>${fmtLetterDate(terminationDate)}</strong>.</p>

  <div class="contract-summary">
    <table>
      ${contractReference ? `<tr style="border-bottom: 1px solid #e5e7eb;"><td style="color:#0f1e3c; font-weight:600;">Service Agreement reference</td><td style="font-family: 'DM Mono', Menlo, monospace; color:#0f1e3c;">${esc(contractReference)}</td></tr>` : ''}
      <tr><td>Contract start date</td><td>${fmtLetterDate(contractStart)}</td></tr>
      <tr><td>Original contract term</td><td>${term} months</td></tr>
      <tr><td>Contract end date</td><td>${fmtLetterDate(calc.contractEndDate)}</td></tr>
      <tr><td>Termination effective date</td><td>${fmtLetterDate(terminationDate)}</td></tr>
      <tr><td>Monthly Recurring Revenue (MRR)</td><td>${fmtUSD(mrr)}</td></tr>
      <tr><td>Months elapsed (complete)</td><td>${calc.monthsElapsed}</td></tr>
      <tr><td>Months remaining</td><td>${calc.monthsRemaining}</td></tr>
    </table>
  </div>

  ${isNoEtf ? `
    <p>Because the termination effective date falls on or after the original contract end date,
    no early termination fee is owed. The Service Agreement will terminate in the ordinary course.</p>
  ` : `
    <p>Per the Service Agreement, the early termination fee owed by Client to Provider is calculated
    as the Monthly Recurring Revenue (MRR) multiplied by the number of months remaining in the original
    contract term:</p>

    <div class="etf-total">
      <div class="label">Early Termination Fee</div>
      <div class="amount">${fmtUSD(calc.etfAmount)}</div>
      <div class="formula">${fmtUSD(mrr)} per month × ${calc.monthsRemaining} months remaining</div>
    </div>

    <p>Payment of the early termination fee is due within thirty (30) days of the termination effective
    date stated above. Upon receipt of payment, Provider will discontinue all services under the Service
    Agreement and the Service Agreement will be considered terminated.</p>
  `}

  <p>Should you have any questions regarding this notice or the calculation above, please contact us at
  the address or phone number listed on this letterhead.</p>

  <div class="signature-block">
    <div class="sig-col">
      <div class="sig-line">${signatureMode === 'two_party'
        ? `<span style="color:white; background:white; display:inline-block; width:280px; height:32px; font-size:18px;">&nbsp;{{signature:1:y}}</span>`
        : ''}</div>
      <div class="sig-label"><strong>${esc(ferrumInfo.name) || 'Ferrum Technology Services'}</strong></div>
      <div class="sig-label">Authorized Signature · ${signatureMode === 'two_party'
        ? `<span style="color:white; background:white; display:inline-block; width:120px; height:14px; font-size:12px;">&nbsp;{{date:1}}</span>`
        : 'Date'}</div>
    </div>
    <div class="sig-col">
      <div class="sig-line">${signatureMode === 'two_party'
        ? `<span style="color:white; background:white; display:inline-block; width:280px; height:32px; font-size:18px;">&nbsp;{{signature:2:y}}</span>`
        : ''}</div>
      <div class="sig-label"><strong>${esc(clientName) || 'Client'}</strong></div>
      <div class="sig-label">Authorized Signature · ${signatureMode === 'two_party'
        ? `<span style="color:white; background:white; display:inline-block; width:120px; height:14px; font-size:12px;">&nbsp;{{date:2}}</span>`
        : 'Date'} (Acknowledgment of Receipt)</div>
    </div>
  </div>

  <div class="footer">
    Generated by Ferrum IQ on ${fmtLetterDate(today)}. This document reflects the early termination
    calculation per the Service Agreement.${letterRef ? ` <span style="font-family: 'DM Mono', Menlo, monospace;">${esc(letterRef)}</span>` : ''}
  </div>
</body>
</html>`;
}

/**
 * Build a concise summary string suitable for a HubSpot deal note.
 * No HTML — HubSpot notes can render markdown-style content but plain
 * text is safest. Lines are pipe-separated header + key facts.
 */
export function buildEtfHubspotNoteBody(opts) {
  const { clientName, mrr, term, contractStart, terminationDate, calculatedBy, contractReference } = opts;
  const calc = calculateEtf({ mrr, term, contractStart, terminationDate });

  const lines = [
    `📋 Early Termination Fee Calculation`,
    `Client: ${clientName || '(unspecified)'}`,
    ...(contractReference ? [`Contract reference: ${contractReference}`] : []),
    `Calculated by: ${calculatedBy || 'Ferrum IQ user'} on ${fmtLetterDate(new Date())}`,
    ``,
    `Contract start: ${fmtLetterDate(contractStart)}`,
    `Contract term: ${term} months`,
    `Termination effective: ${fmtLetterDate(terminationDate)}`,
    `MRR: ${fmtUSD(mrr)}`,
    ``,
    `Months elapsed: ${calc.monthsElapsed}`,
    `Months remaining: ${calc.monthsRemaining}`,
    ``,
    `ETF = ${fmtUSD(mrr)} × ${calc.monthsRemaining} months = ${fmtUSD(calc.etfAmount)}`,
  ];

  if (calc.warnings.length) {
    lines.push('', 'Warnings:');
    calc.warnings.forEach(w => lines.push(`  - ${w}`));
  }

  return lines.join('\n');
}

// Re-export formatters for caller use (page + modal use the same currency
// format as the letter for visual consistency)
export const formatters = { fmtUSD, fmtLetterDate };
